import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { applicationPdfDocumentKeys, applicationPdfDocumentTitles, IVisibleLogic, SettingKey } from 'config'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { getSetting, updateSetting } from 'services'
import { Button, InputVisibleLogic } from 'stories/components'
import { RenderInput } from 'utils/RenderInput'

interface DocumentVisibility {
  visibleLogic: IVisibleLogic[][]
}

type DocumentConfig = Record<string, DocumentVisibility>

const emptyConfig = (): DocumentConfig =>
  applicationPdfDocumentKeys.reduce((acc, key) => {
    acc[key] = { visibleLogic: [] }
    return acc
  }, {} as DocumentConfig)

/**
 * Normalize values loaded from the saved setting, defaulting any missing or
 * malformed entry to "always visible" (empty rules).
 */
const normalizeFromStorage = (raw: unknown): DocumentConfig => {
  const base = emptyConfig()
  if (!raw || typeof raw !== 'object') return base
  Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
    if (!applicationPdfDocumentKeys.includes(key)) return
    const v = value as Partial<DocumentVisibility> | null
    base[key] = { visibleLogic: Array.isArray(v?.visibleLogic) ? v!.visibleLogic! : [] }
  })
  return base
}

export const ApplicationPdf = () => {
  const [isLoading, setLoading] = useState(false)
  const [data, setData] = useState<DocumentConfig>(emptyConfig)
  const [changed, setChanged] = useState(false)

  useEffect(() => {
    initData()
  }, [])

  const initData = async () => {
    setLoading(true)
    try {
      const res = await getSetting(SettingKey.LOAN_APPLICATION_PDF_DOCUMENTS)
      let parsed: unknown = {}
      try {
        parsed = JSON.parse(res?.value || '{}')
      } catch {
        parsed = {}
      }
      setData(normalizeFromStorage(parsed))
      setChanged(false)
    } finally {
      setLoading(false)
    }
  }

  const onChangeLogic = (key: string, logic: IVisibleLogic[][]) => {
    const next = cloneDeep(data)
    next[key] = { visibleLogic: logic }
    setData(next)
    setChanged(true)
  }

  const onSubmit = async () => {
    setLoading(true)
    try {
      await updateSetting(SettingKey.LOAN_APPLICATION_PDF_DOCUMENTS, JSON.stringify(data))
      toast('Successfully saved', { type: 'success' })
      setChanged(false)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />
      <div className="text-sm text-gray-600 mb-4 max-w-3xl">
        Configure which documents are included in the Loan Application PDF that borrowers see and sign. Each document
        can be limited to specific loan/borrower conditions using the visibility rules below. A document with no rules
        is always included. The first matching rule (OR across groups, AND within a group) makes the document visible
        for the current loan.
      </div>

      {applicationPdfDocumentKeys.map((key, index) => (
        <div className="mb-8" key={key}>
          <RenderInput
            Key={`${key}-section`}
            input={{ inputType: 'section', title: `${index + 1}. ${applicationPdfDocumentTitles[key]}` }}
            onChange={() => {}}
          />
          <div className="pl-4 bg-slate-50 p-3 rounded-md">
            <InputVisibleLogic
              id={`${key}-logic`}
              title="Visible Conditions"
              value={data[key]?.visibleLogic || []}
              options={{ hasNotVisible: true }}
              onChange={(value: IVisibleLogic[][]) => onChangeLogic(key, value)}
            />
          </div>
        </div>
      ))}

      <div className="flex justify-center mt-4">
        <Button onClick={onSubmit} loading={isLoading} disabled={!changed}>
          Save Changes
        </Button>
      </div>

      <SaveChanges show={changed} label="Save Changes" onSave={onSubmit} />
    </div>
  )
}
